import { Inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { AppHostService } from '../analytics/app-host.service';
import { DomainMigrationService } from '@domain/domain-migration.service';
import { FeatureFlagKey, FeatureFlagService, FeatureFlagsServiceInjectionToken } from '@feature-flags/feature-flag.service';
import { LoggingService } from '@logging/logging.service';
import { catchError, map, Observable, of, take, timeout } from 'rxjs';

/** Where every route on the retiring domain is sent once the landing page is live. */
export const DOMAIN_MOVED_ROUTE = '/domain-moved';

/** Route `data` key: the route stays reachable on the retiring domain after the cut-over (the landing page itself, logout, ...). */
export const ALLOW_ON_RETIRED_DOMAIN = 'allowOnRetiredDomain';

/** ConfigCat string flag holding the ISO date the landing page goes live. Blank or invalid = off. */
export const DOMAIN_MIGRATION_LANDING_FLAG: FeatureFlagKey = 'domainMigrationLandingPageFrom';

/**
 * How long navigation waits for ConfigCat before giving up and carrying on as normal. getValue$ only emits once
 * ConfigCat has initialised, so without a bound an unreachable ConfigCat would hang every navigation on the old domain.
 */
export const DOMAIN_MIGRATION_LANDING_FLAG_TIMEOUT_MS = 3000;

/** The URLs the Employee Hub native apps open on first launch, before the app host has been recorded. */
export const NATIVE_APP_ENTRY_PATHS = ['/ios-employee-hub', '/android-employee-hub'];

/**
 * From the date in the `domainMigrationLandingPageFrom` flag, sends every navigation on the retiring domain
 * (`environment.domainMigration.oldDomain`) to the "we've moved" page.
 *
 * Fails open: anywhere other than the old domain, before the date, with the flag unset, or if ConfigCat cannot be
 * read in time, navigation proceeds untouched -- so the flag being wrong can never lock people out early.
 *
 * Used as a `canActivateChild` on the root route, ahead of DomainRouteGuard so a visitor is not first bounced to
 * the payroll subdomain. A route opts out with `data: { allowOnRetiredDomain: true }`, which is what stops the
 * landing page redirecting to itself.
 *
 * Not inside the Employee Hub native apps: they load the old domain in a webview and cannot follow the page's advice
 * to move. Mobile browsers get the page like any other browser.
 */
@Injectable({ providedIn: 'root' })
export class DomainMigrationLandingGuard {
  constructor(
    private readonly domainMigration: DomainMigrationService,
    private readonly router: Router,
    private readonly logger: LoggingService,
    private readonly appHost: AppHostService,
    @Inject(FeatureFlagsServiceInjectionToken) private readonly featureFlags: FeatureFlagService
  ) {}

  canActivateChild(childRoute: ActivatedRouteSnapshot, state?: RouterStateSnapshot): Observable<boolean | UrlTree> {
    if (!this.domainMigration.isOnOldDomain() || this.isExempt(childRoute) || this.isNativeApp(state?.url)) {
      return of(true);
    }

    return this.featureFlags.getFlagByCutoffDate$(DOMAIN_MIGRATION_LANDING_FLAG, false, new Date()).pipe(
      take(1),
      timeout(DOMAIN_MIGRATION_LANDING_FLAG_TIMEOUT_MS),
      catchError((error) => {
        this.logger.trace('DOMAIN MIGRATION LANDING GUARD: flag unavailable, not redirecting', error);
        return of(false);
      }),
      map((live) => (live ? this.router.parseUrl(DOMAIN_MOVED_ROUTE) : true))
    );
  }

  private isNativeApp(url: string | undefined): boolean {
    return this.appHost.isNativeApp() || NATIVE_APP_ENTRY_PATHS.some((path) => url?.startsWith(path));
  }

  private isExempt(route: ActivatedRouteSnapshot): boolean {
    for (let current: ActivatedRouteSnapshot | null = route; current; current = current.parent) {
      if (current.data?.[ALLOW_ON_RETIRED_DOMAIN] === true) {
        return true;
      }
    }
    return false;
  }
}
